import { useRef, useState } from 'react'
import { Button, Dialog, Field, Input, Portal, Stack } from '@chakra-ui/react'
import { LuSend } from 'react-icons/lu'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useParams } from 'react-router'
import { useInviteAgendaMember } from '@/hooks/agendas/useInviteAgendaMember'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import type { ApiErrorResponse } from '@/lib/api'

const InviteMemberButton = () => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const inviteMutation = useInviteAgendaMember(agendaId!)

  const validationErrors = (inviteMutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data?.validation_errors

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) inviteMutation.reset()
    setOpen(nextOpen)
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const email = (new FormData(e.currentTarget).get('email') as string).trim()

    inviteMutation.mutate(email, {
      onSuccess: () => {
        showSuccess('Convite enviado com sucesso!')
        handleOpenChange(false)
      },
    })
  }

  return (
    <>
      <Button size={{ base: 'xs', sm: 'sm' }} colorPalette="blue" onClick={() => setOpen(true)}>
        <LuSend />
        Convidar usuário
      </Button>

      {open && (
        <Dialog.Root
          size="sm"
          placement="center"
          open
          onOpenChange={({ open }) => handleOpenChange(open)}
          initialFocusEl={() => inputRef.current}
        >
          <Portal>
            <CustomDialogBackdrop />
            <Dialog.Positioner>
              <Dialog.Content m={6}>
                <form onSubmit={handleSubmit}>
                  <Dialog.Header>
                    <Dialog.Title>Convidar usuário</Dialog.Title>
                  </Dialog.Header>

                  <Dialog.Body>
                    <Stack gap="4">
                      <Field.Root required invalid={!!validationErrors?.email}>
                        <Field.Label>
                          E-mail
                          <Field.RequiredIndicator />
                        </Field.Label>
                        <Input
                          ref={inputRef}
                          name="email"
                          type="email"
                          placeholder="email@exemplo.com"
                          variant="subtle"
                        />
                        {validationErrors?.email?.map((error, index) => (
                          <Field.ErrorText key={index}>{error}</Field.ErrorText>
                        ))}
                      </Field.Root>
                    </Stack>
                  </Dialog.Body>

                  <Dialog.Footer>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      disabled={inviteMutation.isPending}
                    >
                      Cancelar
                    </Button>
                    <Button size="sm" type="submit" colorPalette="primary" loading={inviteMutation.isPending}>
                      Convidar
                    </Button>
                  </Dialog.Footer>
                </form>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      )}
    </>
  )
}

export default InviteMemberButton
